import { ComponentType, SVGProps, useMemo } from 'react';

import { Image } from '@/components/shared/Image';
import { formatNumberWithCommasAndDecimals } from '@/lib/utils/formatNumberWithCommasAndDecimals';
import { getPoolGradientClass } from '@/lib/utils/getPoolGradientClass';
import { getPoolIcon } from '@/lib/utils/getPoolIcon';
import { C_DECIMAL_PLACES } from '@/ts/constants/C_DECIMAL_PLACES';
import { T_Pool } from '@/ts/types/T_Pool';
import { T_TokenPool } from '@/ts/types/T_Profile';
import { T_UserBlockchainData } from '@/ts/types/T_UserBlockchainData';

interface I_UseTokenPoolsProps {
  variant: 'predictions' | 'pools' | 'referrals';
  pools?: T_Pool[];
  blockchainData?: T_UserBlockchainData;
}

export function useTokenPools({
  variant,
  pools,
  blockchainData
}: I_UseTokenPoolsProps) {
  const tokenPools: T_TokenPool[] = useMemo(() => {
    if (variant !== 'pools') {
      return [];
    }

    // Use blockchain data if available
    if (
      blockchainData?.poolBalances &&
      blockchainData.poolBalances.length > 0
    ) {
      return blockchainData.poolBalances.map(poolBalance => {
        const { pool, balanceDecimal } = poolBalance;
        const tokenSymbol = pool.tokenSymbol || pool.poolToken || '';
        const tokenName = pool.tokenName || `Trendex ${tokenSymbol} Pool`;

        // Find matching pool from pools prop to get presaleEndDate
        const matchingPool = pools?.find(
          p =>
            p.poolAddress?.toLowerCase() === pool.poolAddress?.toLowerCase() ||
            p.coinId === pool.coinId ||
            p.tokenSymbol === tokenSymbol
        );

        // Calculate value: balanceDecimal * currentPriceDecimal
        let value = '$0.00';
        if (balanceDecimal && pool.currentPriceDecimal) {
          const balance = parseFloat(balanceDecimal);
          const price = parseFloat(pool.currentPriceDecimal);
          if (!isNaN(balance) && !isNaN(price)) {
            const totalValue = balance * price;
            value = `$${formatNumberWithCommasAndDecimals(
              totalValue.toString(),
              C_DECIMAL_PLACES
            )}`;
          }
        }

        // Format myTokens from balanceDecimal
        const myTokens = balanceDecimal
          ? formatNumberWithCommasAndDecimals(balanceDecimal, C_DECIMAL_PLACES)
          : '0';

        // Get gradient class based on tokenSymbol (TxBTC, TxETH, etc.)
        const gradientClass = getPoolGradientClass(tokenSymbol);

        // Create Icon component - use banner image if available, otherwise use icon
        let Icon: ComponentType<SVGProps<SVGSVGElement>>;
        if (pool.banner?.url) {
          const bannerUrl = pool.banner.url;
          const bannerAlt = pool.banner.alt || tokenName;
          const BannerIcon = (_props: SVGProps<SVGSVGElement>) => (
            <div className="w-10 h-10">
              <Image
                src={bannerUrl}
                alt={bannerAlt}
                width={40}
                height={40}
                className="w-full h-full object-contain"
              />
            </div>
          );
          Icon = BannerIcon as ComponentType<SVGProps<SVGSVGElement>>;
        } else {
          Icon = getPoolIcon(tokenSymbol);
        }

        return {
          Icon,
          name: tokenName,
          ticker: tokenSymbol,
          myTokens,
          value,
          className: gradientClass,
          presaleEndDate: matchingPool?.presaleEndDate,
          poolAddress: pool.poolAddress,
          tokenAddress: pool.poolToken,
          tokenDecimals: matchingPool?.decimals || 18,
          balanceDecimal,
          currentPriceDecimal: pool.currentPriceDecimal
        };
      });
    }

    // Fallback to pools prop if available
    if (pools && pools.length > 0) {
      return pools.map(pool => ({
        Icon: getPoolIcon(pool.tokenSymbol),
        name: pool.title || `Trendex ${pool.tokenSymbol} Pool`,
        ticker: pool.tokenSymbol,
        myTokens: '0', // Will be fetched by TokenPoolCard
        value: '$0.00', // Will be fetched by TokenPoolCard
        className: getPoolGradientClass(pool.tokenSymbol),
        presaleEndDate: pool.presaleEndDate,
        poolAddress: pool.poolAddress,
        tokenAddress: pool.tokenAddress,
        tokenDecimals: pool.decimals
      }));
    }

    // Return empty array if no real data available
    return [];
  }, [variant, pools, blockchainData]);

  return { tokenPools };
}
